import React, { useEffect, useState } from 'react';
import {
  BoxStyled,
  GridStyled,
  BMRSection,
  BMRTitle,
  BMRValue,
  TypographyStyled,
  InputBoxStyled,
  TypographyStyledHeader
} from './style';
import { setPaediatricProtein } from '../../../store/slices/metaDataSlice';
import { useDispatch } from 'react-redux';
import { DoubleTextBlueCardStyled } from '../../../shared/components/doubleTextBlueCardStyled';
import { efsaConstants, comaConstants } from '../../../shared/shared.constants';
import { PlaceholderGrid, CutOffIcon } from '../../../styles/globalStyles';
import { Button, Container, Typography, Box, Modal } from '@mui/material';
import { useAppSelector } from '../../../store/hooks';
import { metaDataSelector } from '../../../store/globalSelectors';
import { NavigationButtons } from '../../../shared/components/navigationButtons';
import { SingleNumberCard } from '../../../shared/components/numberInput/numberCards/singleNumberCard';
import {
  isBetween,
  calcYearsOfAge,
  calcMonthsOfAge
} from '../../../shared/utils/utilFunctions';
// Images
import infoIcon from '../../../assets/icons/infoIcon.svg';
import proteinMuscleDarkIcon from '../../../assets/icons/proteinMuscleDarkIcon.svg';
import proteinMuscleWhiteIcon from '../../../assets/icons/proteinMuscleWhiteIcon.svg';
import { AddOnInfoModal } from '../../../shared/components/modal/addOnInfoModal';

export const PaediatricProtein = () => {
  const { patientInformation, estimatingRequirements } =
    useAppSelector(metaDataSelector);
  const { paediatricProtein } = estimatingRequirements;
  const { age, gender } = patientInformation;
  const dispatch = useDispatch();

  const [openModal1, setOpenModal1] = useState(false);
  const handleOpenModal1 = () => setOpenModal1(true);
  const handleCloseModal1 = () => setOpenModal1(false);
  const [openModal2, setOpenModal2] = useState(false);
  const handleOpenModal2 = () => setOpenModal2(true);
  const handleCloseModal2 = () => setOpenModal2(false);

  const weight =
    patientInformation.metric.estimatedDryWeight ||
    patientInformation.metric.weight;
  const calculatedAgeInYears = calcYearsOfAge(age.years, age.months);
  const calculatedAgeInMonths = calcMonthsOfAge(age.years, age.months);

  useEffect(() => {
    let arVal = 0;
    let priVal = 0;
    let earVal = 0;
    let rniVal = 0;
    if (calculatedAgeInMonths < 132) {
      efsaConstants.age_groups.forEach((e) => {
        if (isBetween(calculatedAgeInMonths, e.minAge, e.maxAge)) {
          arVal = +(e.ar * weight).toFixed(2);
          priVal = +(e.pri * weight).toFixed(2);
        }
      });

      comaConstants.age_groups.forEach((e) => {
        if (isBetween(calculatedAgeInMonths, e.minAge, e.maxAge)) {
          earVal = +e.ear.toFixed(2);
          rniVal = +e.rni.toFixed(2);
        }
      });
    } else {
      efsaConstants.adult_age_groups.forEach((e) => {
        if (isBetween(calculatedAgeInMonths, e.minAge, e.maxAge)) {
          if (gender.toLowerCase() === 'male') {
            arVal = +(e.arMale * weight).toFixed(2);
            priVal = +(e.priMale * weight).toFixed(2);
          } else if (gender.toLowerCase() === 'female') {
            arVal = +(e.arFemale * weight).toFixed(2);
            priVal = +(e.priFemale * weight).toFixed(2);
          }
        }
      });
      comaConstants.adult_age_groups.forEach((e) => {
        if (isBetween(calculatedAgeInMonths, e.minAge, e.maxAge)) {
          if (gender.toLowerCase() === 'male') {
            earVal = +e.earMale.toFixed(2);
            rniVal = +e.rniMale.toFixed(2);
          } else if (gender.toLowerCase() === 'female') {
            earVal = +e.earFemale.toFixed(2);
            rniVal = +e.rniFemale.toFixed(2);
          }
        }
      });
    }

    dispatch(
      setPaediatricProtein({
        ...paediatricProtein,
        efsa: {
          ...paediatricProtein.efsa,
          ar: arVal,
          pri: priVal
        },
        coma: {
          ...paediatricProtein.coma,
          ear: earVal,
          rni: rniVal
        }
      })
    );
  }, [gender, calculatedAgeInMonths, weight]);

  useEffect(() => {
    if (paediatricProtein.proteinRequirement === 0) return;

    const proteinReqAsPerWt = +(
      paediatricProtein.proteinRequirement * weight
    ).toFixed(2);
    const obj = {
      ...paediatricProtein,
      proteinRequirementForWeight: proteinReqAsPerWt
    };
    dispatch(setPaediatricProtein(obj));
  }, [paediatricProtein.proteinRequirement]);

  return (
    <>
      <BoxStyled>
        <Container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            padding: '0 !important'
          }}
        >
          <TypographyStyledHeader>PROTEIN REQUIREMENTS</TypographyStyledHeader>
        </Container>
        <GridStyled container className="width-100">
          <BMRSection>
            <BMRTitle variant="h2">
              {calculatedAgeInMonths > 11 ? 'Age (years):' : 'Age (months):'}
            </BMRTitle>
            <BMRValue>
              {calculatedAgeInMonths > 11
                ? calculatedAgeInYears
                : calculatedAgeInMonths}
            </BMRValue>
          </BMRSection>
          <BMRSection sx={{ marginTop: 0 }}>
            <BMRTitle variant="h2">{'Weight (kg):'}</BMRTitle>
            <BMRValue>{weight}</BMRValue>
          </BMRSection>
        </GridStyled>

        <CutOffIcon>
          <img src={proteinMuscleDarkIcon} alt="muscleScreenTileIcon" />
        </CutOffIcon>
        <PlaceholderGrid container width={'100%'}>
          <TypographyStyled variant="h3">
            INPUT PROTEIN REQUIREMENT
          </TypographyStyled>
          <InputBoxStyled>
            <SingleNumberCard
              width={'100%'}
              subHead={'(g/kg/day)'}
              range={{ min: 0, max: 1000 }}
              incrementDecrementVal={0.01}
              value={paediatricProtein.proteinRequirement}
              setValue={(val: number) => {
                dispatch(
                  setPaediatricProtein({
                    ...paediatricProtein,
                    proteinRequirement: Number(val)
                  })
                );
              }}
            />
          </InputBoxStyled>
          <div style={{ marginTop: '0.9375rem', width: '100%' }}>
            <BMRSection
              sx={{ margin: '0px', backgroundColor: 'rgba(91, 194, 231, 0.5)' }}
            >
              <BMRTitle variant="h2">{'Protein Requirement(g/day):'}</BMRTitle>
              <BMRValue data-testid="prfw" sx={{ color: '#333333' }}>
                {paediatricProtein.proteinRequirementForWeight}
              </BMRValue>
            </BMRSection>
          </div>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              mt: 5,
              width: '100%',
              position: 'relative'
            }}
          >
            <Button
              onClick={() => handleOpenModal1()}
              sx={{
                height: '1.25rem',
                minWidth: '1.25rem',
                padding: 0,
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem'
              }}
            >
              <img src={infoIcon} alt={'infoIcon1'} />
            </Button>
            <DoubleTextBlueCardStyled
              title="EFSA Protein Requirements"
              heading1="AR"
              label1={
                paediatricProtein.efsa.ar === 0
                  ? `NA`
                  : `${paediatricProtein.efsa.ar} g/day`
              }
              heading2="PRI"
              label2={
                paediatricProtein.efsa.pri === 0
                  ? `NA`
                  : `${paediatricProtein.efsa.pri} g/day`
              }
              icon={proteinMuscleWhiteIcon}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              mt: 5,
              width: '100%',
              position: 'relative'
            }}
          >
            <Button
              onClick={() => handleOpenModal2()}
              sx={{
                height: '1.25rem',
                minWidth: '1.25rem',
                padding: 0,
                position: 'absolute',
                top: '1.25rem',
                right: '1.25rem'
              }}
            >
              <img src={infoIcon} alt={'infoIcon2'} />
            </Button>
            <DoubleTextBlueCardStyled
              title="COMA DRV for Protein"
              heading1="EAR"
              label1={
                paediatricProtein.coma.ear === 0
                  ? `NA`
                  : `${paediatricProtein.coma.ear} g/day`
              }
              heading2="RNI"
              label2={
                paediatricProtein.coma.rni === 0
                  ? `NA`
                  : `${paediatricProtein.coma.rni} g/day`
              }
              icon={proteinMuscleWhiteIcon}
            />
          </Box>
        </PlaceholderGrid>
      </BoxStyled>
      <NavigationButtons
        nextDisabled={false}
        nextRoute={'/estimating-requirements/paediatric-fluid'}
      />
      {/*<InfoModal
        open={openModal1}
        onClose={handleCloseModal1}
        title={'Reference:'}
        content={
          <Typography
            align="left"
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.25rem',
              color: '#004F71'
            }}
          >
            EFSA Scientific Opinion on Dietary Reference Values for Protein.
            Published Feb 2012.
            <span style={{ fontFamily: 'CalibriBold' }}> PRI </span>
            is calculated using the average intake plus 1.96-2 times its
            standard deviation. It will meet the requirement of 97.5% of
            individuals in this population.
            <span style={{ fontFamily: 'CalibriBold' }}> AR </span>
            is the average requirement, estimated to satisfy the physiological
            requirement or metabolic demand, as defined by the specified
            criterion for adequacy of protein, in half of the people in a
            population group, given a normal distribution of requirement.
          </Typography>
        }
      />*/}
      <Modal
        open={openModal1}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`EFSA`}
          description1={` = European Food Safety Authority`}
          heading2={`PRI`}
          description2={` = Population Reference Intake`}
          heading3={`AR`}
          description3={` = Average Requirement`}
          extraContent={
            <Typography
              sx={{
                fontFamily: 'Calibri',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                color: '#002A3A'
              }}
            >
              <span style={{ fontFamily: 'CalibriBold' }}> PRI </span>
              is calculated using the average intake plus 1.96-2 times its
              standard deviation. It will meet the requirement of 97.5% of
              individuals in this population.
              <span style={{ fontFamily: 'CalibriBold' }}> AR </span>
              is estimated to satisfy the physiological requirement or metabolic
              demand, as defined by the specified criterion for adequacy of
              protein, in half of the people in a population group, given a
              normal distribution of requirement.
            </Typography>
          }
          title={`Reference:`}
          content={`EFSA NDA Panel (EFSA Panel on Dietetic Products, Nutrition and Allergies), 2012. Scientific Opinion on Dietary Reference Values for protein. EFSA Journal 2012;10(2):2557, 66 pp. doi:10.2903/j.efsa.2012.255`}
          onClose={handleCloseModal1}
        />
      </Modal>
      {/*  <InfoModal
        open={openModal2}
        onClose={handleCloseModal2}
        title={'Reference:'}
        content={
          <Typography
            align="left"
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontWeight: 400,
              lineHeight: '1.25rem',
              color: '#004F71'
            }}
          >
            COMA Dietary Reference Values for Protein. Published by the UK
            Department of Health 1991.
            <span style={{ fontFamily: 'CalibriBold' }}> EAR </span>
            is estimated average requirement is estimated to meet about half the
            population’s requirements.
            <span style={{ fontFamily: 'CalibriBold' }}> RNI </span>
            is the reference nutrition intake, this level of intake should
            satisfy the requirements of 97.5% of healthy individuals in a
            population group.
          </Typography>
        }
      /> */}

      <Modal
        open={openModal2}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`COMA`}
          description1={` = Committee on Medical Aspects of Food and Nutrition`}
          heading2={`DRV`}
          description2={` = Dietary Reference Value`}
          heading3={`EAR`}
          description3={` = Estimated Average Requirement`}
          heading4={`RNI`}
          description4={` = Reference Nutrient Intake`}
          extraContent={
            <Typography
              sx={{
                fontFamily: 'Calibri',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.25rem',
                color: '#002A3A'
              }}
            >
              <span style={{ fontFamily: 'CalibriBold' }}> EAR </span>
              is estimated to meet about half the population’s requirements.
              <span style={{ fontFamily: 'CalibriBold' }}> RNI </span>
              is the level of intake should satisfy the requirements of 97.5% of
              healthy individuals in a population group.
            </Typography>
          }
          title={`Reference:`}
          content={`COMA Dietary Reference Values for Protein. Published by the UK Department of Health 1991.`}
          onClose={handleCloseModal2}
        />
      </Modal>
    </>
  );
};
